import moment from 'moment';
import { segmentLabel } from '../listCategory/presetCatalog';
import { ResolvedSegment } from '../listCategory/type';
import { OpeningHours } from '../stores/models/openingWeek.Model';
import {
  LoyaltyOfferDTO,
  LoyaltyOfferStatus,
  LoyaltyType,
  Multiplicator,
  OfferUsageLimitDTO,
  OfferUsageLimitForm,
  OfferUsagePeriod,
} from './type';

export const timerValues: Array<{ view: string; value: number }> = [
  {
    view: 'Aucun',
    value: 0,
  },
  {
    view: '10m',
    value: 10,
  },
  {
    view: '20m',
    value: 20,
  },
  {
    view: '30m',
    value: 30,
  },
  {
    view: '40m',
    value: 40,
  },
  {
    view: '50m',
    value: 50,
  },
  {
    view: '1h',
    value: 60,
  },
  {
    view: '2h',
    value: 120,
  },
  {
    view: '4h',
    value: 240,
  },
  {
    view: '8h',
    value: 480,
  },
  {
    view: '12h',
    value: 720,
  },
  {
    view: '24h',
    value: 1440,
  },
];
export const getViewFromValue = (value: number) => {
  return timerValues.find(e => e.value === value)?.view;
};

export const hasMultiplicatorsHoursError = (multiplicatorsForm: Multiplicator[]) => {
  for (let i = 0; i < multiplicatorsForm.length; i += 1) {
    const multiplicator = multiplicatorsForm[i];

    for (const hour of multiplicator.hours) {
      if (moment(hour.open).isSameOrAfter(hour.close)) {
        return true;
      }
    }

    for (let j = i + 1; j < multiplicatorsForm.length; j += 1) {
      const otherMultiplicator = multiplicatorsForm[j];

      const sharedDays = multiplicator.slots.some(
        (slot, index) => slot.isSelected && otherMultiplicator.slots[index].isSelected,
      );

      if (sharedDays) {
        for (const hour of multiplicator.hours) {
          for (const betweenHour of otherMultiplicator.hours) {
            if (
              moment(betweenHour.open).isBetween(hour.open, hour.close, undefined, '(]') ||
              moment(betweenHour.close).isBetween(hour.open, hour.close, undefined, '[)') ||
              moment(hour.open).isBetween(betweenHour.open, betweenHour.close, undefined, '()') ||
              moment(hour.close).isBetween(betweenHour.open, betweenHour.close, undefined, '()')
            ) {
              return true;
            }
          }
        }
      }
    }
  }

  return false;
};

export const areHoursOverlapping = (hours: OpeningHours[]) => {
  for (let i = 0; i < hours.length; i += 1) {
    for (let j = i + 1; j < hours.length; j += 1) {
      const hour = hours[i];
      const betweenHour = hours[j];

      if (
        (moment(betweenHour.open).isBetween(hour.open, hour.close, undefined, '(]') ||
          moment(betweenHour.close).isBetween(hour.open, hour.close, undefined, '[)'),
        moment(hour.open).isBetween(betweenHour.open, betweenHour.close, undefined, '()') ||
          moment(hour.close).isBetween(betweenHour.open, betweenHour.close, undefined, '()'))
      ) {
        return true;
      }
    }
  }
  return false;
};

export const isOfferAmountInvalid = (offerValue: number | null | undefined, redeemPointAmount: number | string) => {
  const points = String(redeemPointAmount).trim() === '' ? Number.NaN : Number(redeemPointAmount);
  return Boolean(offerValue && offerValue <= 0) || !Number.isInteger(points) || points < 0;
};

export const DEFAULT_USAGE_LIMIT_FORM: OfferUsageLimitForm = { count: '1', period: OfferUsagePeriod.DAY };

export const DEFAULT_BADGE_USAGE_LIMIT_FORM: OfferUsageLimitForm = { count: '1', period: OfferUsagePeriod.TOTAL };

export const usageLimitToForm = (usageLimit?: OfferUsageLimitDTO | null): OfferUsageLimitForm | null =>
  usageLimit ? { count: String(usageLimit.count), period: usageLimit.period } : null;

export const POSITIVE_INTEGER = /^[1-9]\d*$/;

export const isUsageLimitInvalid = (usageLimit?: OfferUsageLimitForm | null) =>
  Boolean(usageLimit) && !POSITIVE_INTEGER.test(usageLimit?.count.trim() ?? '');

export const toUsageLimitPayload = (usageLimit?: OfferUsageLimitForm | null): OfferUsageLimitDTO | null =>
  usageLimit ? { count: Number(usageLimit.count), period: usageLimit.period } : null;

interface OfferUsageLimitRules {
  usageLimit?: OfferUsageLimitForm | null;
  isBadgeOffer?: boolean;
}

export const offerUsageLimitPayload = ({ usageLimit, isBadgeOffer }: OfferUsageLimitRules): OfferUsageLimitDTO | null =>
  toUsageLimitPayload(isBadgeOffer ? (usageLimit ?? DEFAULT_BADGE_USAGE_LIMIT_FORM) : usageLimit);

export const isZeroPointAmount = (redeemPointAmount: number | string) =>
  String(redeemPointAmount).trim() !== '' && Number(redeemPointAmount) === 0;

interface OfferAccessRules {
  redeemPointAmount: number | string;
  usageLimit?: OfferUsageLimitForm | null;
  isBadgeOffer?: boolean;
}

export const isUnrestrictedZeroPointOffer = ({ redeemPointAmount, usageLimit, isBadgeOffer }: OfferAccessRules) =>
  !isBadgeOffer && isZeroPointAmount(redeemPointAmount) && !usageLimit;

interface BadgeOfferRules {
  isBadgeOffer?: boolean;
  requiredSegmentId?: string | null;
}

export const isBadgeOfferMissingCategory = ({ isBadgeOffer, requiredSegmentId }: BadgeOfferRules) =>
  Boolean(isBadgeOffer) && !requiredSegmentId;

type Translate = (key: string, options?: { defaultValue?: string }) => string;

export const badgeCategoriesOf = (segments: ResolvedSegment[]): ResolvedSegment[] =>
  segments.filter(segment => segment.isEnabled && Boolean(segment.id));

export const badgeCategoryLabel = (segment: Pick<ResolvedSegment, 'label' | 'emoji' | 'presetKey'>, t: Translate) =>
  [segment.emoji, segmentLabel(segment, t)].filter(Boolean).join(' ');

export const isBadgeOfferCategoryUnavailable = (
  { isBadgeOffer, requiredSegmentId }: BadgeOfferRules,
  segments: ResolvedSegment[],
): boolean =>
  Boolean(isBadgeOffer) &&
  Boolean(requiredSegmentId) &&
  segments.length > 0 &&
  !badgeCategoriesOf(segments).some(category => category.id === requiredSegmentId);

export const countOffersRequiringSegment = (
  offers: Pick<LoyaltyOfferDTO, 'status' | 'requiredSegmentId'>[],
  segmentId?: string | null,
): number =>
  segmentId
    ? offers.filter(offer => offer.status === LoyaltyOfferStatus.ACTIVE && offer.requiredSegmentId === segmentId).length
    : 0;

export const isBadgeOfferDTO = (offer: Pick<LoyaltyOfferDTO, 'requiredSegmentId' | 'badge'>): boolean =>
  Boolean(offer.requiredSegmentId || offer.badge);

export const isLockedOffer = (offer: Pick<LoyaltyOfferDTO, 'locked' | 'badgeMissing'>): boolean =>
  offer.locked === true || offer.badgeMissing === true;

export const isUsageLimitReached = (offer: Pick<LoyaltyOfferDTO, 'usageLimitReached'>): boolean =>
  offer.usageLimitReached === true;

export const isOfferUsable = (offer: Pick<LoyaltyOfferDTO, 'locked' | 'badgeMissing' | 'usageLimitReached'>): boolean =>
  !isLockedOffer(offer) && !isUsageLimitReached(offer);

export const isShownToCustomers = (offer: Pick<LoyaltyOfferDTO, 'badgeMissing'>): boolean =>
  offer.badgeMissing !== true;

export const redeemableOffers = (offers: LoyaltyOfferDTO[], points: number): LoyaltyOfferDTO[] =>
  offers.filter(offer => offer.redeemPointAmount <= points).sort((a, b) => a.redeemPointAmount - b.redeemPointAmount);
export const isProductLoyaltyType = (loyaltyType: LoyaltyType) =>
  loyaltyType === LoyaltyType.POS_PRODUCTS || loyaltyType === LoyaltyType.POS_PRODUCTS_AMOUNT;
